import {
  Building2,
  Globe,
  Mail,
  MapPin,
  PenLine,
  Phone,
  Signature,
  UserRound,
  type LucideIcon,
} from 'lucide-react';

export const TYPE_OPTIONS = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'currency', label: 'Currency' },
  { value: 'select', label: 'Dropdown' },
  { value: 'email', label: 'Email' },
  { value: 'url', label: 'Web link' },
] as const;

export type ScalarType = (typeof TYPE_OPTIONS)[number]['value'];

/**
 * The sub-fields a Person decomposes into; each is its own document span/token. Any
 * subset can be marked. `name` is the identity slot (owns the person's label).
 *
 * `signature` is special: it is NOT a fill-in field and is never tokenized or merged —
 * it marks WHERE the person signs. Its selection anchor (originalText + context, stored
 * in SourceSpan) is resolved to real PDF coordinates at signing time (DOCX→PDF).
 */
export type SubKind = 'name' | 'role' | 'company' | 'email' | 'phone' | 'address' | 'signature';

export const SUB_KINDS: SubKind[] = [
  'name',
  'role',
  'company',
  'email',
  'phone',
  'address',
  'signature',
];

export const SUB_LABELS: Record<SubKind, string> = {
  name: 'Name',
  role: 'Role',
  company: 'Company',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  signature: 'Signature',
};

export const SUB_ICON: Record<SubKind, LucideIcon> = {
  name: UserRound,
  role: PenLine,
  company: Building2,
  email: Mail,
  phone: Phone,
  address: MapPin,
  signature: Signature,
};

/**
 * Scalar field type each sub-field maps to (drives the end-user input + merge formatting).
 * `signature` has no fill-in input; 'text' is an inert placeholder — signature behavior is
 * gated everywhere by `subKind`, never by this type.
 */
export const SUB_TYPE: Record<SubKind, ScalarType> = {
  name: 'text',
  role: 'text',
  company: 'text',
  email: 'email',
  phone: 'text',
  address: 'textarea',
  signature: 'text',
};

/**
 * The sub-fields a Company decomposes into — the company counterpart of `SubKind`.
 * `name` is the identity slot (owns the company's label), exactly as it is for a Person.
 *
 * A Company never signs: signing is always done by an authorized-representative Person,
 * so there is deliberately no `signature` slot and no signing email here.
 */
export type CompanySubKind = 'name' | 'address' | 'website' | 'email' | 'phone';

export const COMPANY_SUB_KINDS: CompanySubKind[] = ['name', 'address', 'website', 'email', 'phone'];

export const COMPANY_SUB_LABELS: Record<CompanySubKind, string> = {
  name: 'Company name',
  address: 'Address',
  website: 'Website',
  email: 'Email',
  phone: 'Phone',
};

export const COMPANY_SUB_ICON: Record<CompanySubKind, LucideIcon> = {
  name: Building2,
  address: MapPin,
  website: Globe,
  email: Mail,
  phone: Phone,
};

export const COMPANY_SUB_TYPE: Record<CompanySubKind, ScalarType> = {
  name: 'text',
  address: 'textarea',
  website: 'url',
  email: 'email',
  phone: 'text',
};

/**
 * Company-level metadata, one entry per companyKey — the company counterpart of
 * `PersonMeta`. Children live in `variables`, carrying `companyKey`/`companySubKind`.
 */
export interface CompanyMeta {
  key: string;
  label: string;
}

/** Currencies offered for the Money field type (admin picks one when marking). */
export const CURRENCY_OPTIONS = ['CHF', 'EUR', 'USD', 'GBP', 'JPY', 'AUD', 'CAD'] as const;
export const DEFAULT_CURRENCY = 'CHF';

/** A frozen snapshot of default signer details an admin sets per person. */
export interface DefaultSigner {
  name?: string;
  email?: string;
  role?: string;
  company?: string;
}

export interface MarkedVariable {
  name: string;
  label: string;
  type: ScalarType;
  required: boolean;
  options: string[];
  placeholder?: string;
  helpText?: string;
  /** Currency code (e.g. "CHF") — set only when type === 'currency'. */
  currencyCode?: string;
  /**
   * Name of a source plain field this field mirrors. When set, the filler enters the
   * value once on the source and it fans out to this field at merge time. Plain fields only.
   */
  valueCopyFrom?: string;
  selectedText: string;
  contextBefore: string;
  contextAfter: string;
  paragraphIndex: number | null;
  /**
   * Which occurrence of `selectedText` was marked, and how many the rendered document held
   * then. The tiebreak of last resort: text repeated with identical surroundings (three
   * identical rows of a payment table) is indistinguishable by context, but the admin still
   * clicked exactly one. Undefined on fields marked before this was recorded — those keep
   * resolving by context alone.
   */
  occurrenceIndex?: number | null;
  occurrenceCount?: number | null;
  /**
   * Where this field came from. `'ai'` marks a suggestion the user has not touched yet and
   * drives the badge in the list; editing the field clears it. Presentation only — deliberately
   * NOT persisted and NOT part of `variableFingerprint`, so provenance never dirties a save.
   */
  origin?: 'ai';
  /**
   * Person grouping — present only on a child of a Person (undefined for plain fields).
   * Person-level metadata (label, signatory) lives in the `persons` map keyed by
   * personKey, so a child never holds stale copies of it.
   */
  personKey?: string;
  subKind?: SubKind;
  /**
   * Company grouping — the company counterpart of `personKey`/`subKind`, present only on
   * a child of a Company. A variable is never both a person child and a company child.
   */
  companyKey?: string;
  companySubKind?: CompanySubKind;
}

/**
 * Person-level metadata, one entry per personKey. Children live in `variables`.
 * `isSignatory`/`signingOrder`/`defaultSigner` are owned by the Name sub-field;
 * `companyRef`/`companyLabel` (shared-company link) by the Company sub-field.
 */
export interface PersonMeta {
  key: string;
  label: string;
  isSignatory: boolean;
  signingOrder: number | null;
  /** null = its own company; a ref string = shares another person's company. */
  companyRef: string | null;
  companyLabel: string | null;
  /** Optional pre-filled signer, frozen at author time; null = none. */
  defaultSigner: DefaultSigner | null;
}

export interface DraftSelection {
  selectedText: string;
  contextBefore: string;
  contextAfter: string;
  paragraphIndex: number | null;
  /** Carried straight through to the committed `MarkedVariable`; see the docs there. */
  occurrenceIndex: number | null;
  occurrenceCount: number | null;
  name: string;
  label: string;
  type: ScalarType;
  required: boolean;
  options: string[];
  placeholder: string;
  helpText: string;
  /** Currency code — used when type === 'currency'. */
  currencyCode: string;
  /** Source field name to mirror ('' = none). Plain fields only. */
  valueCopyFrom: string;
  /** Index in `variables` being edited, or null when marking a brand-new field. */
  editingIndex: number | null;
  /**
   * `kind: "person"` turns this selection into a Person SUB-FIELD assigned to
   * `personKey`/`subKind`; `kind: "company"` into a Company SUB-FIELD assigned to
   * `companyKey`/`companySubKind`. The field-mode props above are then ignored.
   */
  kind: 'field' | 'person' | 'company';
  personKey: string;
  personLabel: string;
  subKind: SubKind;
  /** true = create a new person from personKey/personLabel; false = attach to an existing one. */
  newPerson: boolean;
  // Company entity mode. NOTE: distinct from `companyRef`/`companyLabel` below, which are
  // the Person's shared-company link and have nothing to do with Company entities.
  companyKey: string;
  companyEntityLabel: string;
  companySubKind: CompanySubKind;
  /** true = create a new company from companyKey/companyEntityLabel; false = attach to an existing one. */
  newCompany: boolean;
  // Person-level config (Name slot owns signatory + default signer; Company slot owns company link).
  isSignatory: boolean;
  signingOrder: number | null;
  companyRef: string | null;
  companyLabel: string | null;
  defaultSigner: { name: string; email: string; role: string; company: string };
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
